import {
  For,
  Show,
  createMemo,
  createSignal,
  type Component,
  type ParentComponent,
} from 'solid-js';
import type { BetLimit, BetType, TableProfile, WheelStyle, ZeroRule } from '~/domain/types';
import { BET_TYPE_LABELS, PAYOUTS } from '~/domain/bets';
import {
  centsToInput,
  maxChipsFor,
  parseCents,
  payoutDeviations,
  tableWarnings,
  validateTable,
} from '~/domain/profiles';
import { analyseSpread } from '~/domain/spread';

/**
 * Table editor (SPEC.md §5.1).
 *
 * The standard value sits beside every payout field and a deviation is marked
 * while you type, not after saving — and the resulting house edge is recomputed
 * live underneath. That is §9's honesty requirement applied at the point of
 * configuration: you find out a table is bad while entering it.
 */

const ZERO_RULES: { value: ZeroRule; label: string; note: string }[] = [
  { value: 'none', label: 'None', note: 'A zero takes the whole even-money stake' },
  { value: 'la-partage', label: 'La partage', note: 'Half the even-money stake returned immediately' },
  { value: 'en-prison', label: 'En prison', note: 'Even-money stake held for one more spin' },
  { value: 'surrender', label: 'Surrender', note: 'Half returned — the double-zero equivalent' },
];

const EDITABLE_PAYOUTS: BetType[] = [
  'straight', 'split', 'street', 'corner', 'six-line', 'basket', 'column', 'dozen', 'red',
];

export interface TableEditorProps {
  table: TableProfile;
  currency: string;
  onChange: (table: TableProfile) => void;
  onDelete?: () => void;
}

const TableEditor: Component<TableEditorProps> = (props) => {
  const [rawBaseUnit, setRawBaseUnit] = createSignal<string | null>(null);

  const patch = (changes: Partial<TableProfile>) =>
    props.onChange({ ...props.table, ...changes, updatedAt: Date.now() });

  const errors = createMemo(() => validateTable(props.table));
  const warnings = createMemo(() => tableWarnings(props.table));
  const deviations = createMemo(() => new Map(payoutDeviations(props.table).map((d) => [d.betType, d])));

  /**
   * The edge this table gives on a flat even-money bet, recomputed on every
   * keystroke. Even-money because that is where the zero rule shows up.
   */
  const liveEdge = createMemo(() => {
    if (errors().length > 0) return null;
    const analysis = analyseSpread(
      [{ betType: 'red', target: '', chips: 1 }],
      props.table.wheelStyle,
      props.table,
    );
    return analysis.expectedValuePct;
  });

  const standardEdge = () => (props.table.wheelStyle === 'single-zero' ? -1 / 37 : -2 / 38);

  const straightEdge = createMemo(() => {
    if (errors().length > 0) return null;
    return analyseSpread(
      [{ betType: 'straight', target: '17', chips: 1 }],
      props.table.wheelStyle,
      props.table,
    ).expectedValuePct;
  });

  const setPayout = (betType: BetType, value: string) => {
    const next = { ...props.table.payouts };
    const parsed = Number(value);
    if (value.trim() === '' || !Number.isFinite(parsed)) delete next[betType];
    else next[betType] = parsed;
    patch({ payouts: next });
  };

  const setLimit = (group: 'inside' | 'outside', field: keyof BetLimit, value: string) => {
    const cents = parseCents(value);
    if (cents === null) return;
    patch({
      limits: { ...props.table.limits, [group]: { ...props.table.limits[group], [field]: cents } },
    });
  };

  return (
    <div class="space-y-4">
      <Field label="Table name">
        <input
          type="text"
          value={props.table.name}
          onInput={(event) => patch({ name: event.currentTarget.value })}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[12px] text-ink"
        />
      </Field>

      <div class="grid grid-cols-2 gap-3">
        <Field label="Wheel style">
          <select
            value={props.table.wheelStyle}
            onChange={(event) => patch({ wheelStyle: event.currentTarget.value as WheelStyle })}
            class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[12px] text-ink"
          >
            <option value="single-zero">Single zero</option>
            <option value="double-zero">Double zero</option>
          </select>
        </Field>

        <Field label={`Chip value (${props.currency})`} hint="One chip is worth this">
          <input
            type="text"
            inputmode="decimal"
            value={rawBaseUnit() ?? centsToInput(props.table.baseUnit)}
            onInput={(event) => {
              const raw = event.currentTarget.value;
              setRawBaseUnit(raw);
              const cents = parseCents(raw);
              if (cents !== null && cents > 0) patch({ baseUnit: cents });
            }}
            onBlur={() => setRawBaseUnit(null)}
            class="tnum w-full rounded border border-line bg-surface-2 px-2 py-1 text-[12px] text-ink"
          />
        </Field>
      </div>

      <Field label="Zero rule" hint="Applies to even-money bets only">
        <select
          value={props.table.zeroRule}
          onChange={(event) => patch({ zeroRule: event.currentTarget.value as ZeroRule })}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[12px] text-ink"
        >
          <For each={ZERO_RULES}>
            {(rule) => <option value={rule.value}>{rule.label}</option>}
          </For>
        </select>
        <p class="mt-1 text-[10px] text-ink-faint">
          {ZERO_RULES.find((rule) => rule.value === props.table.zeroRule)?.note}
        </p>
      </Field>

      {/* Live edge — §9 at the point of configuration. */}
      <div class="rounded border border-line bg-surface-2 p-2.5">
        <h4 class="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          House edge at this table
        </h4>
        <Show
          when={liveEdge() !== null}
          fallback={<p class="mt-1 text-[11px] text-loss">Fix the errors below to see the edge</p>}
        >
          <div class="mt-1.5 grid grid-cols-2 gap-3 text-[11px]">
            <EdgeReadout label="Even money" actual={liveEdge()!} standard={standardEdge()} />
            <EdgeReadout label="Straight up" actual={straightEdge()!} standard={standardEdge()} />
          </div>
        </Show>
        <Show when={props.table.zeroRule === 'en-prison'}>
          <p class="mt-2 border-t border-line pt-2 text-[10px] leading-relaxed text-ink-faint">
            En prison has the same mean as la partage but a different spread of outcomes.
            This figure assumes a stake meeting a second zero stays imprisoned; where the
            house takes it instead, the edge is −1.39%. Simulation models it spin by spin.
          </p>
        </Show>
      </div>

      <div>
        <h4 class="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Payouts
        </h4>
        <div class="space-y-1">
          <For each={EDITABLE_PAYOUTS}>
            {(betType) => {
              const deviation = () => deviations().get(betType);
              return (
                <div class="flex items-center gap-2 text-[11px]">
                  <span class="w-24 shrink-0 text-ink-dim">{BET_TYPE_LABELS[betType]}</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder={String(PAYOUTS[betType])}
                    value={props.table.payouts[betType] ?? ''}
                    onInput={(event) => setPayout(betType, event.currentTarget.value)}
                    class="tnum w-16 rounded border bg-surface-2 px-1.5 py-0.5 text-ink"
                    classList={{
                      'border-remove': deviation() !== undefined,
                      'border-line': deviation() === undefined,
                    }}
                  />
                  <span class="text-ink-faint">:1</span>
                  <span
                    class="tnum ml-auto text-[10px]"
                    classList={{
                      'text-remove': deviation() !== undefined,
                      'text-ink-faint': deviation() === undefined,
                    }}
                  >
                    {deviation()
                      ? `was ${deviation()!.standard}:1`
                      : `standard ${PAYOUTS[betType]}:1`}
                  </span>
                </div>
              );
            }}
          </For>
        </div>
        <p class="mt-1.5 text-[10px] text-ink-faint">
          Leave blank to use the standard odds. Bet types sharing a payout — columns and
          dozens, all the even-money bets — follow the value set here.
        </p>
      </div>

      <div>
        <h4 class="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Limits
        </h4>
        <For each={['inside', 'outside'] as const}>
          {(group) => (
            <div class="mb-1.5 flex items-center gap-2 text-[11px]">
              <span class="w-24 shrink-0 capitalize text-ink-dim">{group}</span>
              <input
                type="text"
                inputmode="decimal"
                value={centsToInput(props.table.limits[group].min)}
                onChange={(event) => setLimit(group, 'min', event.currentTarget.value)}
                class="tnum w-20 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-ink"
              />
              <span class="text-ink-faint">to</span>
              <input
                type="text"
                inputmode="decimal"
                value={centsToInput(props.table.limits[group].max)}
                onChange={(event) => setLimit(group, 'max', event.currentTarget.value)}
                class="tnum w-24 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-ink"
              />
              <span class="tnum ml-auto text-[10px] text-ink-faint">
                {maxChipsFor(props.table, group === 'inside' ? 'straight' : 'red')} chips max
              </span>
            </div>
          )}
        </For>
        <p class="mt-1 text-[10px] text-ink-faint">
          Inside covers straights through six lines and the basket; outside covers columns,
          dozens and the even-money bets. Amounts are in {props.currency}.
        </p>
      </div>

      <Show when={errors().length > 0}>
        <ul class="space-y-0.5 rounded border border-loss/40 bg-loss/10 p-2 text-[11px] text-loss">
          <For each={errors()}>{(error) => <li>{error}</li>}</For>
        </ul>
      </Show>

      <Show when={errors().length === 0 && warnings().length > 0}>
        <ul class="space-y-0.5 rounded border border-remove/30 bg-remove/10 p-2 text-[11px] text-remove">
          <For each={warnings()}>{(warning) => <li>{warning}</li>}</For>
        </ul>
      </Show>

      <Show when={props.onDelete}>
        <button
          type="button"
          onClick={() => props.onDelete?.()}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim hover:border-loss hover:text-loss"
        >
          Delete table
        </button>
      </Show>
    </div>
  );
};

const Field: ParentComponent<{ label: string; hint?: string }> = (props) => (
  <label class="block">
    <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
      {props.label}
      <Show when={props.hint}>
        <span class="ml-1.5 normal-case tracking-normal opacity-80">{props.hint}</span>
      </Show>
    </span>
    {props.children}
  </label>
);

const EdgeReadout: Component<{ label: string; actual: number; standard: number }> = (props) => {
  const worse = () => props.actual < props.standard - 1e-9;
  const better = () => props.actual > props.standard + 1e-9;
  return (
    <div>
      <div class="text-ink-faint">{props.label}</div>
      <div
        class="tnum text-[15px] font-semibold"
        classList={{
          'text-loss': worse(),
          'text-gain': better(),
          'text-ink': !worse() && !better(),
        }}
      >
        {(props.actual * 100).toFixed(2)}%
      </div>
      <div class="tnum text-[10px] text-ink-faint">
        standard {(props.standard * 100).toFixed(2)}%
        <Show when={worse()}>
          <span class="ml-1 text-loss">worse than standard</span>
        </Show>
        <Show when={better()}>
          <span class="ml-1 text-gain">better than standard</span>
        </Show>
      </div>
    </div>
  );
};

export default TableEditor;
